import { all, call, select, put, takeEvery, spawn } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { format, parse, parseISO } from 'date-fns';

import { waitForModal } from 'components/Modal';
import { trackAddStaffBlock } from 'helpers/events';
import { makePath } from 'helpers/link-generation';
import URLS from 'constants/urls';
import { FULL_ISO_DATE_FORMAT } from 'constants/logic';
import { transformKeysToSnakeCase } from 'helpers/core';
import { practitioners, blockOffTime, resources } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectRouteParams } from 'selectors/general';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { reportError } from 'helpers/error-logging';
import { messages } from './BlockOffTime.messages';

export const SAVE_BLOCK_OFF_TIME = 'SAVE_BLOCK_OFF_TIME';
export const DELETE_BLOCK_OFF_TIME = 'DELETE_BLOCK_OFF_TIME';

export const saveBlockOffTime = ({ date, startTime, endTime, ...restValues }, formAsyncFns) => ({
  type: SAVE_BLOCK_OFF_TIME,
  values: {
    startDatetime: format(parse(startTime, 'HH:mm', parseISO(date)), FULL_ISO_DATE_FORMAT),
    endDatetime: format(parse(endTime, 'HH:mm', parseISO(date)), FULL_ISO_DATE_FORMAT),
    ...restValues,
  },
  formAsyncFns,
});

export const deleteBlockOffTime = id => ({
  type: DELETE_BLOCK_OFF_TIME,
  id,
});

function* trackTimeBlockCreateAction(client, action) {
  const { httpClient } = client;
  const { venueId, itemId } = action;
  const getListEndpoint = URLS.blockOffTime(venueId);
  const response = yield call([httpClient, httpClient.get], getListEndpoint);
  // eslint-disable-next-line camelcase
  const staffBlocksCount = response?.data?.data?.filter(({ item_type }) => item_type === 'practitioner')?.length;

  yield call(trackAddStaffBlock, { totalCount: staffBlocksCount, practitionerId: itemId });
}

export function* doSaveBlockOffTime(client, action) {
  const { httpClient } = client;
  const { values, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const venueId = yield select(selectActiveVenueId);

    const { id, ...restValues } = values;
    const { bookable, description, endDatetime, startDatetime } = restValues;
    const { itemId, itemType } = bookable || {};
    const payload = { itemId, itemType, description, endDatetime, startDatetime };

    const method = id ? 'put' : 'post';
    const endpoint = URLS.blockOffTime(venueId, id);

    if (!id) {
      const confirmed = yield call(waitForModal, client, 'ConfirmStaffBlockModal');
      if (!confirmed) {
        yield call(rejectSubmit);
        return;
      }
    }

    yield call([httpClient, method], endpoint, transformKeysToSnakeCase(payload));

    let successMessage;
    if (id) {
      successMessage = messages.success.blockOffTimeStaffEdited;
    } else {
      successMessage = messages.success.blockOffTimeStaffAdded;
      yield spawn(trackTimeBlockCreateAction, client, { venueId, itemId });
    }
    yield put(createInfoBanner(successMessage));

    const newPath = makePath.appointmentsCalendar({ venueId });
    yield call(client.navigate, newPath); // should be last
    yield call(resolveSubmit);
  } catch (error) {
    yield all([
      yield put(createErrorBanner(messages.error.problemWithRequest)),
      yield call(rejectSubmit),
      yield call(reportError, error),
    ]);
  }
}

export function* doDeleteBlockOffTime(client, action) {
  const { httpClient } = client;
  const { id } = action;

  try {
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.blockOffTime(venueId, id);
    const confirmed = yield call(waitForModal, client, 'DeleteBlockOffTimeModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], endpoint);
      yield put(createInfoBanner(messages.success.blockOffTimeDeleted));
      const newPath = makePath.appointmentsCalendar({ venueId });
      yield call(client.navigate, newPath);
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* handleRouteBlockOffTimeStaffEnter(client) {
  const activeVenueId = yield select(selectActiveVenueId);
  yield call(fetchEntity, practitioners, [client, activeVenueId]);
  yield call(fetchEntity, resources, [client, activeVenueId]);
}

export function* handleRouteEditBlockOffTimeEnter(client) {
  const activeVenueId = yield select(selectActiveVenueId);
  const { id } = yield select(selectRouteParams);

  yield call(fetchEntity, blockOffTime, [client, activeVenueId, id]);
}

export function* watchActions(dependencies) {
  yield takeEvery(SAVE_BLOCK_OFF_TIME, doSaveBlockOffTime, dependencies);
  yield takeEvery(DELETE_BLOCK_OFF_TIME, doDeleteBlockOffTime, dependencies);
}
